import { useState } from 'react'

export default function SmsConsentPage() {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false)
  const imageUrl = "https://certi-prod-bucket-uploads.s3.us-east-1.amazonaws.com/media/Proof+of+consent.png"

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-50 via-white to-gray-50 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight">
            SMS Verification Consent
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 leading-relaxed font-light">
            How CertiAuthentic collects phone number consent for SMS verification
          </p>
        </div>
      </div>

      {/* Overview Section */}
      <div className="max-w-4xl mx-auto py-16 px-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Overview
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            CertiAuthentic is a marketplace for authenticated collectibles and sports memorabilia.
            Users can opt-in to receive SMS verification codes to verify their phone number for
            account security and bidding eligibility on high-value auction items.
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-2">Important:</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-brand-primary mr-2">•</span>
                <span>Messages are <strong>one-time verification codes only</strong> — no recurring messages, marketing, or promotional content</span>
              </li>
              <li className="flex items-start">
                <span className="text-brand-primary mr-2">•</span>
                <span>Users must <strong>explicitly click "Send Code"</strong> each time they want to receive a verification SMS</span>
              </li>
              <li className="flex items-start">
                <span className="text-brand-primary mr-2">•</span>
                <span>Each code expires after 10 minutes and is single-use</span>
              </li>
              <li className="flex items-start">
                <span className="text-brand-primary mr-2">•</span>
                <span>No subscription or recurring messages — each SMS is an isolated transaction</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Consent Flow */}
        <h2 className="text-3xl font-bold text-gray-900 mb-8">
          User Consent Flow
        </h2>

        <div className="space-y-8 mb-12">
          {/* Step 1 */}
          <div className="flex gap-6 items-start group">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-xl bg-gray-900 flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:bg-brand-primary transition-all duration-300">
                <span className="text-2xl font-extrabold text-white">1</span>
              </div>
            </div>
            <div className="flex-1 pt-2">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                User Navigates to Account Settings
              </h3>
              <p className="text-gray-600 leading-relaxed">
                After logging in, users access their account settings page where they can manage
                their profile information and security preferences.
              </p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex gap-6 items-start group">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-xl bg-gray-900 flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:bg-brand-primary transition-all duration-300">
                <span className="text-2xl font-extrabold text-white">2</span>
              </div>
            </div>
            <div className="flex-1 pt-2">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                User Enters Phone Number
              </h3>
              <p className="text-gray-600 leading-relaxed">
                In the Phone Verification section, users select their country code and enter their
                mobile phone number in the provided form fields.
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex gap-6 items-start group">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-xl bg-gray-900 flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:bg-brand-primary transition-all duration-300">
                <span className="text-2xl font-extrabold text-white">3</span>
              </div>
            </div>
            <div className="flex-1 pt-2">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                User Clicks "Send Code" Button
              </h3>
              <p className="text-gray-600 leading-relaxed">
                By clicking the "Send Code" button, the user explicitly consents to receive a
                one-time verification code via SMS to the phone number they provided.
              </p>
            </div>
          </div>

          {/* Step 4 */}
          <div className="flex gap-6 items-start group">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-xl bg-gray-900 flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:bg-brand-primary transition-all duration-300">
                <span className="text-2xl font-extrabold text-white">4</span>
              </div>
            </div>
            <div className="flex-1 pt-2">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                User Receives SMS with Verification Code
              </h3>
              <p className="text-gray-600 leading-relaxed">
                The user receives a text message containing a 6-digit verification code that expires
                in 10 minutes. This is a one-time code for the specific verification request.
              </p>
            </div>
          </div>

          {/* Step 5 */}
          <div className="flex gap-6 items-start group">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-xl bg-gray-900 flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:bg-brand-primary transition-all duration-300">
                <span className="text-2xl font-extrabold text-white">5</span>
              </div>
            </div>
            <div className="flex-1 pt-2">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                User Enters Code to Complete Verification
              </h3>
              <p className="text-gray-600 leading-relaxed">
                The user enters the 6-digit code in the verification field to confirm ownership of
                the phone number, completing the verification process.
              </p>
            </div>
          </div>
        </div>

        {/* Screenshot Proof */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Visual Proof of Consent
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            Below is a screenshot of the phone verification interface where users explicitly opt-in
            to receive SMS verification codes:
          </p>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <button
              onClick={() => setIsImageModalOpen(true)}
              className="w-full cursor-pointer hover:opacity-90 transition-opacity duration-200 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 rounded-lg"
              type="button"
            >
              <img
                src={imageUrl}
                alt="Phone Verification Consent Interface Screenshot"
                className="w-full rounded-lg shadow-md"
              />
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-4 text-center">
            Screenshot showing the phone verification section in user account settings
            <span className="block mt-1 text-brand-primary font-medium">Click image to view full size</span>
          </p>
        </div>

        {/* Sample Message */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Sample SMS Message
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            This is an example of the actual SMS message users receive:
          </p>
          <div className="bg-gray-900 text-white rounded-lg p-6 font-mono text-sm">
            Your Certi verification code is: 123456
          </div>
          <p className="text-sm text-gray-500 mt-3">
            Note: This is a one-time verification code. Users only receive this message when they explicitly click "Send Code" in their account settings.
          </p>
        </div>

        {/* Transactional Nature */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Transactional SMS Only
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-4">
            CertiAuthentic's SMS system is purely transactional:
          </p>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="text-brand-primary font-bold mr-3">•</span>
              <span><strong>No subscription required</strong> — Users are not "subscribed" to any SMS list</span>
            </li>
            <li className="flex items-start">
              <span className="text-brand-primary font-bold mr-3">•</span>
              <span><strong>One message per request</strong> — Each time a user clicks "Send Code," they receive exactly one SMS</span>
            </li>
            <li className="flex items-start">
              <span className="text-brand-primary font-bold mr-3">•</span>
              <span><strong>No recurring messages</strong> — We never send follow-up, reminder, or automated messages</span>
            </li>
            <li className="flex items-start">
              <span className="text-brand-primary font-bold mr-3">•</span>
              <span><strong>No marketing or promotions</strong> — Only verification codes for account security</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="bg-gray-50 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Questions about our SMS verification?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Contact our support team for more information
          </p>
          <a
            href="mailto:contact@certiauthentic.com"
            className="inline-block bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            Contact Support
          </a>
        </div>
      </div>

      {/* Image Modal */}
      {isImageModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
          onClick={() => setIsImageModalOpen(false)}
        >
          <div className="relative max-w-7xl max-h-[90vh] w-full">
            <button
              onClick={() => setIsImageModalOpen(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors text-4xl font-light"
              type="button"
            >
              ×
            </button>
            <img
              src={imageUrl}
              alt="Phone Verification Consent Interface Screenshot - Full Size"
              className="w-full h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  )
}
